import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdDevices, MdMonitorHeart, MdRefresh } from 'react-icons/md';
import DeviceHealthDetail from './DeviceHealthDetail';
import HardwareCatalog from './HardwareCatalog';
import { StatusPill, fetchJson, format, groupByCategory, relativeTime, tone, worst } from './deviceHealth';
import { lightOnline } from './lightOrganization';
import './deviceMonitoring.css';

const REFRESH_MS = 30000;
const FILTERS = [['all', 'All devices'], ['problems', 'Needs attention'], ['offline', 'Offline']];

function Metric({ component, children }) {
  return <span className={`dm-metric dm-metric--${tone(component?.status)}`}>{children}</span>;
}

function FleetRow({ device, selected, onSelect, now }) {
  const { components = {}, metrics = {} } = device;
  const parts = Object.values(components).filter(c => c.category).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const byProbe = probe => parts.filter(c => c.probe === probe);
  const status = items => (items.length ? worst(items) : undefined);
  const silent = device.overall === 'offline' || device.overall === 'no_data';
  return <tr className={selected ? 'is-selected' : undefined} onClick={() => onSelect(device.device_id)}>
    <td>
      <button type="button" className="dm-device-link" onClick={event => { event.stopPropagation(); onSelect(device.device_id); }}>
        <span className={`dm-dot dm-dot--${silent ? 'neutral' : tone(device.overall)}`} aria-hidden="true" />{device.public_id}
      </button>
      {device.organization_name && <small>{device.organization_name}</small>}
      {!device.hardware_confirmed && <small className="dm-confirm">Confirm hardware</small>}
    </td>
    <td><StatusPill status={device.overall} /></td>
    {silent ? <td colSpan={5} className="dm-muted">{device.overall === 'no_data' ? 'Health agent not installed or never reported' : `No report since ${relativeTime(device.report_received_at, now)}`}</td> : <>
      <td><Metric component={components.system}>{format(metrics.cpu_temp_c, ' °C')}</Metric></td>
      <td><Metric component={status(byProbe('fan_tach'))}>{typeof metrics.fan_rpm === 'number' ? `${metrics.fan_rpm} rpm` : '—'}</Metric></td>
      <td><Metric component={status(byProbe('pi5_power'))}>{format(metrics.input_v, ' V', 2)}</Metric></td>
      <td><Metric component={components.disk}>{format(metrics.disk_used_percent, '%')}</Metric></td>
      <td><div className="dm-hardware">{groupByCategory(parts).map(([category, items]) => <StatusPill key={category} status={worst(items).status}>{category}</StatusPill>)}</div></td>
    </>}
    <td className="dm-muted">{relativeTime(device.report_received_at, now)}</td>
  </tr>;
}

export default function DeviceMonitoring({ light = false }) {
  const [fleet, setFleet] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('devices');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFleet(await fetchJson('/admin/device-health'));
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const devices = useMemo(() => {
    const all = fleet?.devices || [];
    return light ? lightOnline(all, d => !['offline', 'no_data'].includes(d.overall),
      (d, online) => (online ? { ...d, overall: 'ok', report_received_at: fleet.generated_at } : { ...d, overall: 'offline' })) : all;
  }, [fleet, light]);
  const counts = useMemo(() => ({
    online: devices.filter(d => !['offline', 'no_data'].includes(d.overall)).length,
    critical: devices.filter(d => d.overall === 'critical').length,
    warning: devices.filter(d => d.overall === 'warning').length,
    silent: devices.filter(d => ['offline', 'no_data'].includes(d.overall)).length,
  }), [devices]);
  const visible = devices.filter(d => filter === 'all' || (filter === 'offline' ? ['offline', 'no_data'].includes(d.overall) : ['critical', 'warning'].includes(d.overall)));
  const now = fleet?.generated_at ? new Date(fleet.generated_at).getTime() : Date.now();

  return <section className="device-monitoring" aria-labelledby="dm-title">
    <header className="dm-header">
      <div className="dm-heading">
        <span className="dm-heading-icon"><MdMonitorHeart aria-hidden="true" size={26} /></span>
        <div><h1 id="dm-title">Device monitoring</h1><p>Hardware and system health, reported by each device every minute.</p></div>
      </div>
      <button type="button" className="dm-button" onClick={load} disabled={loading}><MdRefresh aria-hidden="true" size={18} />{loading ? 'Refreshing…' : 'Refresh'}</button>
    </header>

    <div className="dm-tabs" role="tablist" aria-label="Monitoring views">
      {[['devices', 'Devices'], ['catalog', 'Hardware catalog']].map(([key, label]) =>
        <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}>{label}</button>)}
    </div>

    {tab === 'catalog' ? <HardwareCatalog /> : <>

    {error && <div className="dm-error" role="alert">{error}</div>}

    <div className="dm-kpis">
      <div><span>Online</span><strong>{counts.online} / {devices.length}</strong></div>
      <div className="dm-kpi--critical"><span>Critical</span><strong>{counts.critical}</strong></div>
      <div className="dm-kpi--warning"><span>Warnings</span><strong>{counts.warning}</strong></div>
      <div><span>Offline or no agent</span><strong>{counts.silent}</strong></div>
    </div>

    <div className="dm-filters" role="group" aria-label="Filter devices">
      {FILTERS.map(([key, label]) => <button key={key} type="button" aria-pressed={filter === key} onClick={() => setFilter(key)}>{label}</button>)}
    </div>

    {!fleet && !error ? <p className="dm-muted">Loading devices…</p> : visible.length === 0 ? (
      <div className="dm-empty"><MdDevices aria-hidden="true" size={28} /><p>{devices.length ? 'No devices match this filter.' : 'No enrolled devices yet.'}</p></div>
    ) : <div className="dm-table-wrap">
      <table className="dm-table">
        <thead><tr><th>Device</th><th>Status</th><th>CPU</th><th>Fan</th><th>Power</th><th>Disk</th><th>Hardware</th><th>Last report</th></tr></thead>
        <tbody>{visible.map(device => <FleetRow key={device.device_id} device={device} selected={device.device_id === selectedId} onSelect={setSelectedId} now={now} />)}</tbody>
      </table>
    </div>}

    {selectedId && <DeviceHealthDetail deviceId={selectedId} refreshKey={fleet?.generated_at} onClose={() => setSelectedId(null)} />}
    </>}
  </section>;
}
